
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PolicyAcceptance from '../components/PolicyAcceptance';
import { setAuthToken, sanitizeInput } from '@/utils/authUtils';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [cookieAccepted, setCookieAccepted] = useState(false);
  
  useEffect(() => {
    console.log('Login page mounted');
    console.log('Current URL:', window.location.href);
    
    const checkSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session check error:', error);
          return;
        }
        
        if (data.session) {
          console.log('User already has active session, redirecting to jobs page');
          navigate('/jobs');
        } else {
          console.log('No active session found on login page');
        }
      } catch (err) {
        console.error('Failed to check session:', err);
      }
    };
    
    checkSession();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setAuthError(null);
    
    console.log('Login attempt with email:', email);

    if (!email || !password) {
      toast.error('Please enter both email and password');
      setLoading(false);
      return;
    }
    
    if (!termsAccepted || !privacyAccepted || !cookieAccepted) {
      toast.error('Please accept all policies to continue');
      setLoading(false);
      return;
    }

    try {
      // For testing purposes - allow mock login in development
      if (process.env.NODE_ENV !== 'production') {
        console.log('Using development mode login');
        
        // Simple mock login for testing
        setAuthToken('mock-token', 'candidate');
        localStorage.setItem('userEmail', email);
        
        toast.success('Login successful (dev mode)');
        navigate('/jobs');
        setLoading(false);
        return;
      }

      console.log('Attempting Supabase login');
      const { data, error } = await supabase.auth.signInWithPassword({
        email: sanitizeInput(email),
        password,
      });

      if (error) {
        console.error('Login error:', error);
        setAuthError(error.message || 'Authentication failed');
        toast.error(error.message || 'Authentication failed');
      } else {
        console.log('Login successful, redirecting');
        toast.success('Login successful');
        navigate('/jobs');
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('An unexpected error occurred during login');
      toast.error('An unexpected error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex flex-grow items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8 pt-28">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-2xl font-bold">Sign In</CardTitle>
            <CardDescription className="text-center">
              Sign in to view your TrueApply application status
            </CardDescription>
            {authError && (
              <div className="mt-2 p-2 bg-red-50 text-red-600 text-sm rounded">
                Error: {authError}
              </div>
            )}
          </CardHeader>
          <form onSubmit={handleLogin}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="flex items-center space-x-2">
                  <Lock className="h-4 w-4 text-gray-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
              
              <PolicyAcceptance
                termsChecked={termsAccepted}
                privacyChecked={privacyAccepted}
                cookieChecked={cookieAccepted}
                onTermsChange={setTermsAccepted}
                onPrivacyChange={setPrivacyAccepted}
                onCookieChange={setCookieAccepted}
                className="pt-2"
              />
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading || !termsAccepted || !privacyAccepted || !cookieAccepted}
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
              <p className="text-center text-sm text-gray-500">
                Don't have an account?{" "}
                <Link to="/signup" className="text-blue-600 hover:text-blue-500">
                  Sign up
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default Login;
